import React, { useEffect, useMemo, useState } from 'react'
import {
    Box,
    Checkbox,
    CircularProgress,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import { useQuery } from '@tanstack/react-query'

import { axiosInstance } from '../../../helpers'
import type { LeasingSeasonListItem } from '../types/leasingSeason.types'

const useStyles = makeStyles((theme: Theme) => ({
    section: {
        marginTop: theme.spacing(1),
    },
    sectionTitle: {
        fontWeight: 600,
        marginBottom: theme.spacing(0.5),
    },
    note: {
        color: theme.palette.text.secondary,
        fontSize: '0.8125rem',
        marginBottom: theme.spacing(1),
    },
    field: {
        width: '100%',
        marginTop: theme.spacing(1),
    },
    list: {
        marginTop: theme.spacing(1.5),
        maxHeight: 220,
        overflowY: 'auto',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1),
    },
    row: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(1),
        padding: theme.spacing(1, 1.5),
        borderBottom: `1px solid ${theme.palette.divider}`,
        '&:last-child': {
            borderBottom: 'none',
        },
    },
    rowBody: {
        minWidth: 0,
        flex: 1,
    },
    rowName: {
        fontWeight: 600,
        fontSize: '0.875rem',
    },
    rowMeta: {
        color: theme.palette.text.secondary,
        fontSize: '0.75rem',
    },
    toolbar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
        marginTop: theme.spacing(1),
    },
    empty: {
        padding: theme.spacing(2),
        color: theme.palette.text.secondary,
        fontSize: '0.8125rem',
    },
}))

export interface RolloverCandidate {
    id: number
    name: string
    email: string
    phone: string
    pipeline_stage: string
    lead_source: string
}

interface RolloverCandidatesResponse {
    count: number
    results: RolloverCandidate[]
}

function stageLabel(stage: string): string {
    return stage.replace(/_/g, ' ')
}

interface SeasonLeadRolloverPanelProps {
    seasons: LeasingSeasonListItem[]
    enabled: boolean
    onEnabledChange: (enabled: boolean) => void
    sourceSeasonId: number | ''
    onSourceSeasonIdChange: (seasonId: number | '') => void
    selectedLeadIds: number[]
    onSelectedLeadIdsChange: (leadIds: number[]) => void
}

export const SeasonLeadRolloverPanel: React.FC<SeasonLeadRolloverPanelProps> = ({
    seasons,
    enabled,
    onEnabledChange,
    sourceSeasonId,
    onSourceSeasonIdChange,
    selectedLeadIds,
    onSelectedLeadIdsChange,
}) => {
    const classes = useStyles()
    const [search, setSearch] = useState('')

    const candidatesQuery = useQuery({
        queryKey: [
            'leasing-season-rollover-candidates',
            sourceSeasonId,
            search,
        ],
        queryFn: async () => {
            const {
                data,
            } = await axiosInstance.get<RolloverCandidatesResponse>(
                `/outreach/leasing-seasons/${sourceSeasonId}/rollover-candidates/`,
                {
                    params: search.trim()
                        ? { search: search.trim() }
                        : undefined,
                },
            )
            return data
        },
        enabled: enabled && sourceSeasonId !== '',
        staleTime: 30_000,
    })

    const candidates = candidatesQuery.data?.results ?? []
    const selectedSet = useMemo(() => new Set(selectedLeadIds), [
        selectedLeadIds,
    ])

    useEffect(() => {
        if (!enabled) {
            onSelectedLeadIdsChange([])
            return
        }
        if (sourceSeasonId === '' && seasons.length > 0) {
            const current =
                seasons.find((season) => season.is_current) ?? seasons[0]
            onSourceSeasonIdChange(current.id)
        }
    }, [
        enabled,
        onSelectedLeadIdsChange,
        onSourceSeasonIdChange,
        seasons,
        sourceSeasonId,
    ])

    const allVisibleSelected =
        candidates.length > 0 &&
        candidates.every((lead) => selectedSet.has(lead.id))

    const toggleAllVisible = () => {
        if (allVisibleSelected) {
            const visible = new Set(candidates.map((lead) => lead.id))
            onSelectedLeadIdsChange(
                selectedLeadIds.filter((id) => !visible.has(id)),
            )
            return
        }
        const merged = new Set(selectedLeadIds)
        candidates.forEach((lead) => merged.add(lead.id))
        onSelectedLeadIdsChange(Array.from(merged))
    }

    const toggleLead = (leadId: number) => {
        if (selectedSet.has(leadId)) {
            onSelectedLeadIdsChange(
                selectedLeadIds.filter((id) => id !== leadId),
            )
            return
        }
        onSelectedLeadIdsChange([...selectedLeadIds, leadId])
    }

    if (seasons.length === 0) {
        return null
    }

    return (
        <Box className={classes.section}>
            <Typography className={classes.sectionTitle}>
                Lead rollover
            </Typography>
            <Typography className={classes.note}>
                Optionally bring open leads from a prior season. They reset to
                the Leads stage; signed and future residents are excluded.
                Applications, invites, and screening are not copied.
            </Typography>
            <FormControlLabel
                control={
                    <Checkbox
                        color="primary"
                        checked={enabled}
                        onChange={(e) => onEnabledChange(e.target.checked)}
                    />
                }
                label="Roll leads forward from a prior season"
            />

            {enabled && (
                <>
                    <FormControl
                        variant="outlined"
                        size="small"
                        className={classes.field}
                    >
                        <InputLabel id="rollover-source-season-label">
                            Source season
                        </InputLabel>
                        <Select
                            labelId="rollover-source-season-label"
                            label="Source season"
                            value={sourceSeasonId === '' ? '' : sourceSeasonId}
                            onChange={(e) => {
                                const value = e.target.value
                                onSourceSeasonIdChange(
                                    value === '' ? '' : Number(value),
                                )
                                onSelectedLeadIdsChange([])
                            }}
                        >
                            {seasons.map((season) => (
                                <MenuItem key={season.id} value={season.id}>
                                    {season.name}
                                    {season.is_current ? ' (current)' : ''}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        className={classes.field}
                        size="small"
                        variant="outlined"
                        label="Search leads"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />

                    <Box className={classes.toolbar}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    color="primary"
                                    checked={allVisibleSelected}
                                    indeterminate={
                                        !allVisibleSelected &&
                                        candidates.some((lead) =>
                                            selectedSet.has(lead.id),
                                        )
                                    }
                                    onChange={toggleAllVisible}
                                    disabled={candidates.length === 0}
                                />
                            }
                            label="Select all visible"
                        />
                        <Typography variant="caption" color="textSecondary">
                            {selectedLeadIds.length} selected
                        </Typography>
                    </Box>

                    <Box className={classes.list}>
                        {candidatesQuery.isLoading ? (
                            <Box display="flex" justifyContent="center" py={3}>
                                <CircularProgress size={24} />
                            </Box>
                        ) : candidates.length === 0 ? (
                            <Typography className={classes.empty}>
                                No open leads available to roll from this
                                season.
                            </Typography>
                        ) : (
                            candidates.map((lead) => (
                                <Box key={lead.id} className={classes.row}>
                                    <Checkbox
                                        color="primary"
                                        size="small"
                                        checked={selectedSet.has(lead.id)}
                                        onChange={() => toggleLead(lead.id)}
                                    />
                                    <Box className={classes.rowBody}>
                                        <Typography className={classes.rowName}>
                                            {lead.name}
                                        </Typography>
                                        <Typography className={classes.rowMeta}>
                                            {stageLabel(lead.pipeline_stage)}
                                            {lead.email
                                                ? ` · ${lead.email}`
                                                : ''}
                                            {lead.phone
                                                ? ` · ${lead.phone}`
                                                : ''}
                                        </Typography>
                                    </Box>
                                </Box>
                            ))
                        )}
                    </Box>
                </>
            )}
        </Box>
    )
}
